import type {
  Panel,
  PanelLayoutReport,
  PanelRow,
} from "@electro-planner/domain";

/**
 * Bygger en SVG-sida med central-layout: varje DIN-skena ritas som en
 * horisontell stripe med moduler markerade, och varje placerad rad som
 * en färgkodad box i rätt bredd (TE-moduler). Används som sida 3 i
 * PDF-exporten.
 */

const PAGE_W = 210;
const MARGIN = 10;

const TITLE_H = 18;
const RAIL_LABEL_H = 5;
const RAIL_H = 16;
const RAIL_GAP = 10;
const UNPLACED_GAP = 8;

export function buildPanelRailLayout({
  panel,
  layout,
}: {
  panel: Panel | undefined;
  layout: PanelLayoutReport;
}): string {
  const usableW = PAGE_W - 2 * MARGIN;
  const parts: string[] = [];

  const title = `${panel?.designation ?? ""} ${panel?.title ?? "Central"}`.trim();
  parts.push(
    `<text x="${MARGIN}" y="${MARGIN + 6}" font-size="5.5" font-weight="700" fill="#0f172a">${escape(title)} — layout</text>`,
  );
  const meta = [
    `${layout.rails.length} skenor`,
    `${panel?.modulesPerRail ?? 12} TE/skena`,
    layout.anyOverflow ? "⚠ överfylld skena" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  parts.push(
    `<text x="${MARGIN}" y="${MARGIN + 12}" font-size="3" fill="#475569">${escape(meta)}</text>`,
  );

  let y = MARGIN + TITLE_H;

  for (const rail of layout.rails) {
    // Skena-etikett + TE-räknare
    parts.push(
      `<text x="${MARGIN}" y="${y + 3.5}" font-size="3.2" font-weight="600" fill="#0f172a">Skena ${rail.railNumber}</text>`,
    );
    const countColor = rail.overflow ? "#be123c" : "#64748b";
    parts.push(
      `<text x="${MARGIN + usableW}" y="${y + 3.5}" font-size="2.8" text-anchor="end" fill="${countColor}">${rail.used} / ${rail.capacity} TE</text>`,
    );
    y += RAIL_LABEL_H;

    const displayModules = Math.max(rail.capacity, rail.used);
    const modW = usableW / displayModules;
    const railY = y;

    // Bakgrundsstripe (skenans ram)
    parts.push(
      `<rect x="${MARGIN}" y="${railY}" width="${usableW}" height="${RAIL_H}" fill="#f8fafc" stroke="#94a3b8" stroke-width="0.35" rx="0.6"/>`,
    );

    // Om det finns överfyllnad — måla rosa bakgrund över den del som är
    // utanför kapaciteten.
    if (rail.overflow) {
      const overflowX = MARGIN + rail.capacity * modW;
      const overflowW = usableW - rail.capacity * modW;
      parts.push(
        `<rect x="${overflowX}" y="${railY}" width="${overflowW}" height="${RAIL_H}" fill="#fecaca" stroke="none"/>`,
      );
    }

    // TE-rutnät — tunna streckade linjer var modul
    for (let i = 1; i < displayModules; i++) {
      const xGrid = MARGIN + i * modW;
      parts.push(
        `<line x1="${xGrid}" y1="${railY}" x2="${xGrid}" y2="${railY + RAIL_H}" stroke="#cbd5e1" stroke-width="0.2" stroke-dasharray="0.8 0.6"/>`,
      );
    }

    // Boxar för varje placerad rad
    for (const { row, modules, startPosition } of rail.slots) {
      const boxX = MARGIN + (startPosition - 1) * modW;
      const boxW = modules * modW;
      parts.push(renderBox(row, boxX, railY, boxW, RAIL_H));
    }

    y += RAIL_H + RAIL_GAP;
  }

  if (layout.unplaced.length > 0) {
    y += UNPLACED_GAP;
    parts.push(
      `<text x="${MARGIN}" y="${y}" font-size="3.2" font-weight="600" fill="#475569">Ej placerade (${layout.unplaced.length})</text>`,
    );
    y += 3;
    let cx = MARGIN;
    const chipW = 24;
    const chipH = 8;
    for (const row of layout.unplaced) {
      if (cx + chipW > MARGIN + usableW) {
        cx = MARGIN;
        y += chipH + 2;
      }
      parts.push(renderBox(row, cx, y, chipW, chipH));
      cx += chipW + 2;
    }
    y += chipH + 4;
  }

  const totalH = y + MARGIN;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}mm" height="${totalH}mm" viewBox="0 0 ${PAGE_W} ${totalH}" font-family="system-ui, -apple-system, 'Segoe UI', sans-serif"><rect width="${PAGE_W}" height="${totalH}" fill="white"/>${parts.join("")}</svg>`;
}

type Category = "DB" | "JFB" | "JFA" | "EB" | "LB" | "OTHER";

const CATEGORY_LABEL: Record<Category, string> = {
  DB: "MCB",
  JFB: "RCD",
  JFA: "RCBO",
  EB: "MCCB",
  LB: "SW",
  OTHER: "—",
};

const CATEGORY_FILL: Record<Category, string> = {
  DB: "#dbeafe",
  JFB: "#fef9c3",
  JFA: "#dcfce7",
  EB: "#fecaca",
  LB: "#e2e8f0",
  OTHER: "#f3f4f6",
};

function categorize(row: PanelRow): Category {
  const comp = (row.component ?? "").toUpperCase();
  if (comp.startsWith("JFA") || comp.startsWith("RCBO")) return "JFA";
  if (comp.startsWith("JFB") || comp.startsWith("RCD")) return "JFB";
  if (comp.startsWith("DB") || comp.startsWith("MCB")) return "DB";
  if (comp.startsWith("EB") || comp.startsWith("MCCB")) return "EB";
  if (comp.startsWith("LB") || comp.startsWith("LBS")) return "LB";
  return "OTHER";
}

function renderBox(
  row: PanelRow,
  x: number,
  y: number,
  w: number,
  h: number,
): string {
  const cat = categorize(row);
  const fill = CATEGORY_FILL[cat];
  const cx = x + w / 2;
  const specLabel = [
    row.ampRating !== undefined ? `${row.ampRating}A` : "",
    row.poles !== undefined ? `${row.poles}P` : "",
    row.characteristic ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  // Smala boxar (1 TE ≈ 14 mm på A4 bredd) får bara beteckningen; bredare
  // får spec-rad också.
  const bodyLabel = w > 12 ? specLabel : "";
  const bodyLabel2 = w > 18 ? CATEGORY_LABEL[cat] : "";

  return `
    <g>
      <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}" stroke="#0f172a" stroke-width="0.3" rx="0.5"/>
      <text x="${cx}" y="${y + 4}" font-size="2.8" font-weight="700" text-anchor="middle" fill="#0f172a">${escape(row.designation)}</text>
      ${bodyLabel ? `<text x="${cx}" y="${y + 8}" font-size="2.2" text-anchor="middle" fill="#334155">${escape(bodyLabel)}</text>` : ""}
      ${bodyLabel2 ? `<text x="${cx}" y="${y + 11.8}" font-size="2" text-anchor="middle" fill="#64748b">${escape(bodyLabel2)}</text>` : ""}
    </g>
  `;
}

function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
